import React, { useEffect, useState } from "react";
import { useNotify } from "../../components/ConfirmModal";
import { PageHead } from "../../pages/work/WorkUi";
import PortalSelect from "../PortalSelect";
import billingApi, { billingError } from "./billingApi";
import { useBillingMeta } from "./BillingUi";
import { FINANCE_SECTIONS } from "../../utils/financeTerms";
import "../../pages/dm/dm.css";
import "../../pages/work/work.css";
import "./billing.css";

/** Finance > Finance Settings: invoice numbering, due days, tax and how clients pay you. */
function TenantFinanceSettings() {
  const notify = useNotify();
  const { meta, reload } = useBillingMeta();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const canManage = !!meta?.access?.manage;

  useEffect(() => {
    billingApi
      .settings()
      .then((s) =>
        setForm({
          Prefix: s.Prefix || "",
          Next_No: s.Next_No,
          Due_Days: s.Due_Days,
          Default_Tax_Pct: s.Default_Tax_Pct ?? "",
          Tax_Label: s.Tax_Label || "GST",
          Currency_Id: s.Currency?.Currency_Id || "",
          Payment_Instructions: s.Payment_Instructions || "",
          Terms: s.Terms || "",
        })
      )
      .catch((err) => setError(billingError(err, "Could not load settings")));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await billingApi.saveSettings(form);
      notify.success("Finance settings saved");
      reload();
    } catch (err) {
      notify.error(billingError(err, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dmp wk bl">
      <PageHead
        title={FINANCE_SECTIONS.settings.label}
        info={`${FINANCE_SECTIONS.settings.hint}. These defaults are used on every new invoice you send to your clients.`}
      />
      <div className="dmp-card">
        <h3 className="dmp-card__title">Invoice defaults</h3>
        {error ? <div className="dmp-error">{error}</div> : null}
        {!form ? (
          <div className="wk-muted">Loading…</div>
        ) : (
          <form onSubmit={save} className="bl-form-grid">
            <fieldset disabled={!canManage || saving} style={{ display: "contents" }}>
              <label className="wk-field">
                Number prefix
                <input value={form.Prefix} onChange={set("Prefix")} maxLength={20} placeholder="INV-" />
              </label>
              <label className="wk-field">
                Next number
                <input type="number" min={1} value={form.Next_No} onChange={set("Next_No")} required />
                <span className="wk-field__hint">
                  Next invoice: {form.Prefix}
                  {String(form.Next_No || 1).padStart(4, "0")}
                </span>
              </label>
              <label className="wk-field">
                Due in (days)
                <input type="number" min={0} max={365} value={form.Due_Days} onChange={set("Due_Days")} required />
              </label>
              <label className="wk-field">
                Default tax %
                <input type="number" min={0} max={100} step="0.01" value={form.Default_Tax_Pct} onChange={set("Default_Tax_Pct")} placeholder="18" />
              </label>
              <label className="wk-field">
                Tax name
                <input value={form.Tax_Label} onChange={set("Tax_Label")} maxLength={20} placeholder="GST" />
              </label>
              <label className="wk-field">
                Currency
                <PortalSelect value={form.Currency_Id} onChange={set("Currency_Id")} aria-label="Currency">
                  <option value="">Default (INR)</option>
                  {(meta?.currencies || []).map((c) => (
                    <option key={c.Currency_Id} value={c.Currency_Id}>
                      {c.Code} {c.Symbol ? `(${c.Symbol})` : ""}
                    </option>
                  ))}
                </PortalSelect>
              </label>
              <label className="wk-field bl-span-2">
                How clients pay you (printed on invoices)
                <textarea
                  value={form.Payment_Instructions}
                  onChange={set("Payment_Instructions")}
                  rows={3}
                  placeholder="Bank: HDFC, A/c 1234567890, IFSC HDFC0001234 · UPI: company@hdfcbank"
                />
              </label>
              <label className="wk-field bl-span-2">
                Default terms
                <textarea value={form.Terms} onChange={set("Terms")} rows={2} placeholder="Payment due within 15 days." />
              </label>
            </fieldset>
            {canManage ? (
              <div className="bl-span-2">
                <button type="submit" className="dmp-btn" disabled={saving}>
                  {saving ? "Saving…" : "Save settings"}
                </button>
              </div>
            ) : (
              <p className="wk-muted bl-span-2">Only people who manage billing can change these settings.</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export default TenantFinanceSettings;
